import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./notes.css";
import { useNavigate } from "react-router-dom";

export default function Notes() {
  const [outing, setOuting] = useState("Loading...");
  const [workHour, setWorkHour] = useState("Loading...");
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [lastClockInTime, setLastClockInTime] = useState(null);
  const [completedSeconds, setCompletedSeconds] = useState(0); // backend worked time in seconds
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("accessToken");

  // Fixed: Proper 12-hour formatting (no "PM AM")
  const formatTime = (time) => {
    if (!time) return "N/A";
    // If it already contains AM/PM, just return it
    if (time.toLowerCase().includes("am") || time.toLowerCase().includes("pm")) return time;

    let [hours, minutes] = time.split(":");
    let hour = parseInt(hours, 10);
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${period}`;
  };

  const formatDuration = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h} Hr ${m} Min ${s} Sec`;
  };

  const updateLiveTimer = () => {
    if (!isClockedIn || !lastClockInTime) return;

    const now = new Date();

    // Parse lastClockInTime (e.g., "12:34 PM")
    const [timePart, period] = lastClockInTime.split(" ");
    let [h, m] = timePart.split(":").map(Number);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;

    const clockIn = new Date();
    clockIn.setHours(h, m, 0, 0);

    const diffSeconds = Math.max(0, Math.floor((now - clockIn) / 1000));

    // Break logic: 12:50 PM to 1:30 PM (40 minutes)
    // Values in minutes from midnight
    const breakStartMins = 12 * 60 + 50; // 770
    const breakEndMins = 13 * 60 + 30;   // 810

    const clockInMins = h * 60 + m;
    const nowMins = now.getHours() * 60 + now.getMinutes();

    const overlapStart = Math.max(clockInMins, breakStartMins);
    const overlapEnd = Math.min(nowMins, breakEndMins);
    const overlapSeconds = Math.max(0, (overlapEnd - overlapStart) * 60);

    const liveWorkedSeconds = Math.max(0, diffSeconds - overlapSeconds);
    setWorkHour(formatDuration(completedSeconds + liveWorkedSeconds));
  };

  const fetchData = async () => {
    if (!userId || !token) return;

    const today = new Date().toISOString().split("T")[0];

    try {
      const response = await axios.get(
        `https://hrmsbackend.prosevo.com/api/attendance/employee-worked-hours/${userId}/?start_date=${today}&end_date=${today}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      setOuting(formatTime(data.expected_check_out));

      const workedHoursStr = data.worked_hours?.[0]?.worked_hours || "00:00:00";
      const [wh, wm, ws] = workedHoursStr.split(":").map(Number);
      const totalCompletedSeconds = (wh || 0) * 3600 + (wm || 0) * 60 + (ws || 0);

      // last_punch_direction: 0 is IN, 1 is OUT
      if (data.last_punch_direction === 0) {
        setIsClockedIn(true);
        setLastClockInTime(data.last_punch);
        setCompletedSeconds(totalCompletedSeconds);
      } else {
        setIsClockedIn(false);
        setWorkHour(formatDuration(totalCompletedSeconds));
      }
    } catch (err) {
      console.error(err);
      setWorkHour("Error");
      setOuting("Error");
    }
  };

  useEffect(() => {
    fetchData();
    const fetchDataInterval = setInterval(fetchData, 3 * 60 * 1000);
    return () => clearInterval(fetchDataInterval);
  }, [userId, token]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (isClockedIn) {
      updateLiveTimer();
      intervalRef.current = setInterval(updateLiveTimer, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isClockedIn, lastClockInTime, completedSeconds]);

  const getWorkHourColor = () => {
    if (!workHour || workHour.includes("Error") || workHour === "Loading...") return "#610d0dba";
    const h = parseInt(workHour.match(/(\d+) Hr/)?.[1] || "0", 10);
    return h >= 8 ? "#20a519ee" : "#610d0dba";
  };

  const handleviewmore = () => {
    navigate(`/user/dayList/${userId}`);
  };
  return (
    <div className="noteParent">
      <div className=" d-flex w-100 justify-content-between align-items-center pb-4">
        <div className="noteHead">Today's Report</div>
        <a onClick={handleviewmore} className="text-decoration-none" style={{ color: "var(--primary-color)", fontWeight: '600' }}>View all</a>
      </div>

      <div className="noteContainer d-flex flex-column gap-3">
        {/* Expected Outing - Modern Design */}
        <div className="note d-flex justify-content-between flex-wrap px-2 py-2">
          <div className="d-flex align-items-center justify-content-start gap-3">
            <img src="/assets/run.gif" alt="Running" style={{ width: "36px", height: "36px", filter: 'grayscale(1)' }} />
            <div className="d-flex flex-column">
              <span className="text-nowrap" style={{ fontSize: "14px", fontWeight: "500", color: "var(--text-muted)" }}>
                Expected Outing
              </span>
              <span className="fw-bold" style={{ color: 'var(--text-main)', fontSize: "15px" }}>
                {outing}
              </span>
            </div>
          </div>
        </div>

        {/* Working Hours - Modern Design */}
        <div className="note d-flex justify-content-between flex-wrap px-2 py-2">
          <div className="d-flex align-items-center justify-content-start gap-3">
            <img src="/assets/work.gif" alt="Working" style={{ width: "36px", height: "36px", filter: 'grayscale(1)' }} />
            <div className="d-flex flex-column">
              <span className="text-nowrap" style={{ fontSize: "14px", fontWeight: "500", color: "var(--text-muted)" }}>
                Working Hours
              </span>
              <span
                className="fw-bold"
                style={{ color: getWorkHourColor(), fontSize: "15px" }}
              >
                {workHour}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}